/**
 * Reviews Page (standalone /pages/reviews-page template only)
 *
 * Uses Klaviyo Client Reviews API (public company_id only).
 * Official onsite endpoint: GET /client/reviews
 * Docs: https://developers.klaviyo.com/en/reference/get_client_reviews
 *
 * - Review media field: attributes.images (relative keys → S3 URLs)
 * - Star distribution: rating-only scan of all published reviews (not first page)
 * - Klaviyo's generated cursor currently returns HTTP 500 after page one for
 *   this account. Load More transparently falls back to created-date keyset
 *   pagination, preserving chronological order without loading all reviews.
 */
(function () {
  'use strict';

  var REVISION = '2026-07-15';
  var PAGE_SIZE = 20;
  // Use fast.a.klaviyo.com directly — a.klaviyo.com 301 redirects break browser CORS.
  var CLIENT_BASE = 'https://fast.a.klaviyo.com/client';
  var REVIEW_IMAGE_BASE = 'https://klaviyo.s3.amazonaws.com/reviews/images/';
  var SORT_MAP = {
    recent: '-created',
    highest: '-rating',
    lowest: 'rating',
  };

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function formatNumber(n) {
    try {
      return Number(n).toLocaleString();
    } catch (e) {
      return String(n);
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function starsMarkup(rating) {
    var r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    var html = '';
    for (var i = 1; i <= 5; i++) {
      html += i <= r ? '★' : '☆';
    }
    return html;
  }

  function formatDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  }

  function productHref(product) {
    if (!product) return '';
    var url = product.url || '';
    if (!url) return '';
    try {
      var parsed = new URL(url, window.location.origin);
      return parsed.pathname + parsed.search;
    } catch (e) {
      return url;
    }
  }

  function extractCursor(nextUrl) {
    if (!nextUrl) return null;
    try {
      var u = new URL(nextUrl);
      return u.searchParams.get('page[cursor]');
    } catch (e) {
      return null;
    }
  }

  // Client API returns relative keys; Klaviyo widgets resolve via this S3 prefix.
  function resolveReviewImageUrl(path) {
    if (!path) return '';
    var value = String(path).trim();
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    return REVIEW_IMAGE_BASE + value.replace(/^\//, '');
  }

  function isVideoUrl(url) {
    return /\.(mp4|webm|mov)(\?|$)/i.test(url || '');
  }

  function emptyDistribution() {
    return {
      5: { count: 0, percent: 0 },
      4: { count: 0, percent: 0 },
      3: { count: 0, percent: 0 },
      2: { count: 0, percent: 0 },
      1: { count: 0, percent: 0 },
    };
  }

  function buildDistribution(counts, total) {
    var dist = emptyDistribution();
    var t = total || 0;
    for (var star = 1; star <= 5; star++) {
      var c = counts[star] || 0;
      dist[star] = {
        count: c,
        percent: t > 0 ? Math.round((c / t) * 100) : 0,
      };
    }
    return dist;
  }

  function ReviewsPage(root) {
    this.root = root;
    this.companyId = (root.dataset.companyId || '').trim();
    this.proxyEndpoint = (root.dataset.proxyEndpoint || '').trim();
    this.starColor = root.dataset.starColor || '#c4a35a';
    this.verifiedLabel = root.dataset.verifiedLabel || 'Verified Buyer';
    this.emptyMessage = root.dataset.emptyMessage || 'No reviews found.';
    this.errorMessage = root.dataset.errorMessage || 'Unable to load reviews. Please try again later.';

    this.els = {
      total: qs(root, '[data-crp-total]'),
      average: qs(root, '[data-crp-average]'),
      scoreStars: qs(root, '[data-crp-score-stars]'),
      scoreCount: qs(root, '[data-crp-score-count]'),
      bars: qs(root, '[data-crp-bars]'),
      list: qs(root, '[data-crp-list]'),
      sort: qs(root, '[data-crp-sort]'),
      loadMore: qs(root, '[data-crp-load-more]'),
      loading: qs(root, '[data-crp-loading]'),
      error: qs(root, '[data-crp-error]'),
      empty: qs(root, '[data-crp-empty]'),
      toolbarCount: qs(root, '[data-crp-toolbar-count]'),
    };

    this.state = {
      sort: 'recent',
      nextCursor: null,
      hasMore: false,
      loading: false,
      seenIds: Object.create(null),
      totalReviews: 0,
      averageRating: 0,
      distribution: emptyDistribution(),
      distributionReady: false,
      keysetMode: false,
      keysetBoundary: null,
      ratingTier: null,
    };

    this.root.style.setProperty('--crp-star', this.starColor);
  }

  ReviewsPage.prototype.init = function () {
    if (!this.companyId && !this.proxyEndpoint) {
      this.showError('Missing Klaviyo public company ID. Add it in the section settings.');
      return;
    }

    var self = this;
    if (this.els.sort) {
      this.els.sort.addEventListener('change', function () {
        self.state.sort = self.els.sort.value || 'recent';
        self.resetAndLoad();
      });
    }
    if (this.els.loadMore) {
      this.els.loadMore.addEventListener('click', function () {
        self.loadReviews(false);
      });
    }

    this.resetAndLoad();
    this.loadSummary();
  };

  ReviewsPage.prototype.setStateVisibility = function (mode) {
    var map = {
      loading: this.els.loading,
      error: this.els.error,
      empty: this.els.empty,
    };
    Object.keys(map).forEach(function (key) {
      if (!map[key]) return;
      map[key].hidden = key !== mode;
    });
  };

  ReviewsPage.prototype.showError = function (message) {
    this.setStateVisibility('error');
    if (this.els.error) {
      this.els.error.textContent = message || this.errorMessage;
    }
    if (this.els.loadMore) this.els.loadMore.hidden = true;
    console.error('[ReviewsPage]', message || this.errorMessage);
  };

  ReviewsPage.prototype.resetAndLoad = function () {
    this.state.nextCursor = null;
    this.state.hasMore = false;
    this.state.seenIds = Object.create(null);
    this.state.keysetMode = false;
    this.state.keysetBoundary = null;
    this.state.ratingTier =
      this.state.sort === 'highest' ? 5 : this.state.sort === 'lowest' ? 4 : null;
    // Rating sorts use deterministic rating-tier keyset pagination from the
    // start. Most Recent first tries Klaviyo's documented cursor.
    this.state.keysetMode = this.state.sort !== 'recent';
    if (this.els.list) this.els.list.innerHTML = '';
    this.loadReviews(true);
  };

  ReviewsPage.prototype.apiHeaders = function () {
    return {
      Accept: 'application/vnd.api+json',
      revision: REVISION,
    };
  };

  ReviewsPage.prototype.fetchJson = function (url) {
    return fetch(url, { headers: this.apiHeaders(), credentials: 'omit' }).then(function (res) {
      if (!res.ok) {
        return res.text().then(function (body) {
          var err = new Error('Request failed (' + res.status + ')');
          err.status = res.status;
          err.body = body ? body.slice(0, 300) : '';
          throw err;
        });
      }
      return res.json();
    });
  };

  ReviewsPage.prototype.buildClientReviewsUrl = function (cursor) {
    var params = new URLSearchParams();
    params.set('company_id', this.companyId);
    params.set('page[size]', String(PAGE_SIZE));
    params.set('sort', SORT_MAP[this.state.sort] || SORT_MAP.recent);
    params.set(
      'filter',
      'and(equals(status,"published"),greater-or-equal(rating,4))'
    );
    if (cursor) params.set('page[cursor]', cursor);
    return CLIENT_BASE + '/reviews?' + params.toString();
  };

  ReviewsPage.prototype.buildFeaturedMediaUrl = function () {
    var params = new URLSearchParams();
    params.set('company_id', this.companyId);
    params.set('page[size]', '6');
    params.set('sort', '-created');
    params.set(
      'filter',
      'and(equals(status,"published"),greater-or-equal(rating,4),has(media))'
    );
    return CLIENT_BASE + '/reviews?' + params.toString();
  };

  ReviewsPage.prototype.buildKeysetReviewsUrl = function () {
    var params = new URLSearchParams();
    var boundary = this.state.keysetBoundary;
    var sort = this.state.sort;
    var filters = [
      'equals(status,"published")',
      'greater-or-equal(rating,4)',
    ];

    // Ask for one extra row because the boundary row is included by
    // less-or-equal and removed by the review-ID dedupe guard.
    params.set('company_id', this.companyId);
    params.set('page[size]', String(boundary ? PAGE_SIZE + 1 : PAGE_SIZE));

    if (sort === 'recent') {
      params.set('sort', '-created');
      if (boundary) filters.push('less-or-equal(created,' + boundary + ')');
    } else {
      // Preserve rating ordering without relying on the broken cursor:
      // walk 5→1 or 1→5, newest first inside each rating tier.
      params.set('sort', '-created');
      filters.push('equals(rating,' + this.state.ratingTier + ')');
      if (boundary) filters.push('less-or-equal(created,' + boundary + ')');
    }

    params.set(
      'filter',
      filters.length > 1 ? 'and(' + filters.join(',') + ')' : filters[0]
    );
    return CLIENT_BASE + '/reviews?' + params.toString();
  };

  ReviewsPage.prototype.buildProxyUrl = function (cursor) {
    var url = new URL(this.proxyEndpoint, window.location.origin);
    url.searchParams.set('page_size', String(PAGE_SIZE));
    url.searchParams.set('sort', this.state.sort);
    if (cursor) url.searchParams.set('cursor', cursor);
    url.searchParams.set('include_summary', cursor ? '0' : '1');
    return url.toString();
  };

  ReviewsPage.prototype.loadSummary = function () {
    var self = this;

    if (this.proxyEndpoint) {
      return;
    }

    var reportParams = new URLSearchParams();
    reportParams.set('company_id', this.companyId);
    reportParams.set('group_by', 'company_id');
    reportParams.set(
      'statistics',
      'average_rating,total_reviews,total_ratings,total_questions'
    );
    reportParams.set('timeframe', 'all_time');

    this.fetchJson(CLIENT_BASE + '/review-values-reports?' + reportParams.toString())
      .then(function (payload) {
        var results =
          payload &&
          payload.data &&
          payload.data.attributes &&
          payload.data.attributes.results
            ? payload.data.attributes.results
            : [];
        var stats = results[0] && results[0].statistics ? results[0].statistics : null;
        if (!stats) return;

        self.state.totalReviews = Number(stats.total_reviews || stats.total_ratings || 0);
        self.state.averageRating = Number(stats.average_rating || 0);
        self.renderSummaryTotals();
      })
      .catch(function (err) {
        console.error('[ReviewsPage] summary request failed', err && err.message);
      });

    this.loadDistributionFromClientApi();
  };

  ReviewsPage.prototype.loadDistributionFromClientApi = function () {
    var self = this;
    var cacheKey = 'crp_dist_v5_' + this.companyId;
    var counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    var total = 0;
    var seenIds = Object.create(null);
    var boundary = null;
    var safety = 0;
    var maxPages = 200;

    function requestPage() {
      var params = new URLSearchParams();
      // "all" is required for parity with Klaviyo's account-level
      // total_reviews statistic. Non-rating question records are ignored
      // below and will trigger the required mismatch warning.
      var filters = ['equals(status,"all")'];
      params.set('company_id', self.companyId);
      params.set('page[size]', '100');
      params.set('sort', '-created');
      params.set('fields[review]', 'rating,created');
      if (boundary) {
        filters.push('less-or-equal(created,' + boundary + ')');
      }
      params.set(
        'filter',
        filters.length > 1 ? 'and(' + filters.join(',') + ')' : filters[0]
      );
      return self.fetchJson(CLIENT_BASE + '/reviews?' + params.toString());
    }

    function next() {
      if (safety++ >= maxPages) {
        console.warn('[ReviewsPage] distribution scan safety limit reached');
        return;
      }

      requestPage()
        .then(function (payload) {
          var rows = (payload && payload.data) || [];
          var newRows = 0;
          var oldestCreated = null;

          rows.forEach(function (row) {
            if (!row || !row.id || seenIds[row.id]) return;
            seenIds[row.id] = true;
            newRows += 1;
            var rating = row && row.attributes ? Number(row.attributes.rating) : 0;
            var created = row && row.attributes ? row.attributes.created : null;
            if (created) oldestCreated = created;
            if (rating >= 1 && rating <= 5) {
              counts[rating] += 1;
              total += 1;
            }
          });

          if (!rows.length || rows.length < 100) {
            finish();
            return;
          }

          if (newRows === 0 && boundary) {
            // All rows had the inclusive boundary timestamp. Move one
            // millisecond back so the scan cannot loop forever.
            boundary = new Date(new Date(boundary).getTime() - 1).toISOString();
          } else if (oldestCreated) {
            boundary = oldestCreated;
          } else {
            console.warn('[ReviewsPage] distribution scan has no created boundary');
            return;
          }

          // Stay below Klaviyo's 10/s burst limit.
          window.setTimeout(next, 175);
        })
        .catch(function (err) {
          console.error('[ReviewsPage] distribution scan failed', err && err.message, err && err.status);
        });
    }

    function finish() {
      if (total <= 0) return;

      function commit() {
        var expected = self.state.totalReviews;
        if (!expected) {
          window.setTimeout(commit, 250);
          return;
        }
        if (total !== expected) {
          console.warn(
            '[ReviewsPage] Rating distribution mismatch:',
            total,
            'rating rows vs',
            expected,
            'total review records. Percentages use rated reviews only; Klaviyo question records have no 1–5 rating.'
          );
        }

        self.state.distribution = buildDistribution(counts, total);
        self.state.distributionReady = true;
        self.renderDistribution();
        try {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              total: total,
              distribution: self.state.distribution,
            })
          );
        } catch (e) {
          // Ignore quota / private-mode failures.
        }
      }

      commit();
    }

    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.distribution && parsed.total > 0) {
          self.state.distribution = parsed.distribution;
          self.state.distributionReady = true;
          self.renderDistribution();
          return;
        }
      }
    } catch (e) {
      // Ignore storage errors and scan again.
    }

    next();
  };

  ReviewsPage.prototype.renderSummaryTotals = function () {
    var total = this.state.totalReviews;
    var avg = this.state.averageRating;
    var avgDisplay = avg ? avg.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1') : '—';

    if (this.els.total) {
      this.els.total.textContent =
        total > 0 ? 'Based on ' + formatNumber(total) + ' reviews' : 'Based on reviews';
    }
    if (this.els.average) this.els.average.textContent = avgDisplay;
    if (this.els.scoreStars) {
      this.els.scoreStars.innerHTML = starsMarkup(avg);
      this.els.scoreStars.setAttribute('aria-label', avgDisplay + ' out of 5 stars');
    }
    if (this.els.scoreCount) {
      this.els.scoreCount.textContent =
        total > 0 ? formatNumber(total) + ' reviews' : '';
    }
    if (this.els.toolbarCount) {
      this.els.toolbarCount.textContent =
        total > 0 ? formatNumber(total) + ' reviews' : '';
    }
  };

  ReviewsPage.prototype.renderDistribution = function () {
    if (!this.els.bars) return;
    var dist = this.state.distribution;
    var rows = this.els.bars.querySelectorAll('[data-crp-bar]');
    rows.forEach(function (row) {
      var star = row.getAttribute('data-crp-bar');
      // 1–3 star reviews are intentionally excluded from this page.
      var item =
        Number(star) < 4
          ? { count: 0, percent: 0 }
          : dist[star] || { count: 0, percent: 0 };
      var fill = row.querySelector('[data-crp-bar-fill]');
      var meta = row.querySelector('[data-crp-bar-meta]');
      if (fill) fill.style.width = item.percent + '%';
      if (meta) meta.textContent = item.percent + '% (' + formatNumber(item.count) + ')';
    });
  };

  ReviewsPage.prototype.normalizeProxyPayload = function (payload) {
    if (!payload) return { reviews: [], nextCursor: null, summary: null, phase: null };

    if (payload.reviews) {
      return {
        reviews: payload.reviews,
        nextCursor: payload.pagination && payload.pagination.nextCursor
          ? payload.pagination.nextCursor
          : null,
        summary: payload.summary || null,
        phase: payload.pagination && payload.pagination.phase
          ? payload.pagination.phase
          : null,
      };
    }

    return this.normalizeKlaviyoPayload(payload);
  };

  ReviewsPage.prototype.normalizeKlaviyoPayload = function (payload) {
    var rows = (payload && payload.data) || [];
    var reviews = [];
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (!row || row.type !== 'review') continue;
      var a = row.attributes || {};
      if (a.review_type === 'question') continue;
      // Reviews Page intentionally displays only 4- and 5-star reviews.
      if (Number(a.rating) < 4) continue;

      var images = Array.isArray(a.images)
        ? a.images
            .map(resolveReviewImageUrl)
            .filter(function (url) {
              return !!url;
            })
        : [];

      reviews.push({
        id: row.id,
        rating: a.rating,
        title: a.title,
        content: a.content,
        author: a.author,
        verified: !!a.verified,
        created: a.created,
        product: a.product || null,
        images: images,
      });
    }
    return {
      reviews: reviews,
      nextCursor: extractCursor(payload && payload.links ? payload.links.next : null),
      summary: null,
      phase: null,
    };
  };

  ReviewsPage.prototype.applySummary = function (summary) {
    if (!summary) return;
    if (typeof summary.totalReviews === 'number') {
      this.state.totalReviews = summary.totalReviews;
    }
    if (typeof summary.averageRating === 'number') {
      this.state.averageRating = summary.averageRating;
    }
    if (summary.distribution) {
      this.state.distribution = summary.distribution;
      this.state.distributionReady = true;
      this.renderDistribution();
    }
    this.renderSummaryTotals();
  };

  ReviewsPage.prototype.loadReviews = function (isInitial) {
    var self = this;
    if (this.state.loading) return;
    if (!isInitial && !this.state.hasMore) return;

    this.state.loading = true;
    if (isInitial) this.setStateVisibility('loading');
    if (this.els.loadMore) {
      this.els.loadMore.classList.add('is-loading');
      this.els.loadMore.setAttribute('aria-busy', 'true');
    }

    var url = this.proxyEndpoint
      ? this.buildProxyUrl(isInitial ? null : this.state.nextCursor)
      : this.state.keysetMode
        ? this.buildKeysetReviewsUrl()
        : this.buildClientReviewsUrl(isInitial ? null : this.state.nextCursor);

    function processPayload(payload) {
        var normalized = self.proxyEndpoint
          ? self.normalizeProxyPayload(payload)
          : self.normalizeKlaviyoPayload(payload);

        if (normalized.summary) self.applySummary(normalized.summary);

        var appended = 0;
        var oldestCreated = null;
        normalized.reviews.forEach(function (review) {
          if (review && review.created) oldestCreated = review.created;
          if (!review || !review.id) return;
          if (Number(review.rating) < 4) return;
          if (self.state.seenIds[review.id]) return;
          self.state.seenIds[review.id] = true;
          self.els.list.insertAdjacentHTML('beforeend', self.renderCard(review));
          appended += 1;
        });

        if (!self.proxyEndpoint && self.state.keysetMode) {
          var rawCount = payload && payload.data ? payload.data.length : 0;
          var requestedSize = self.state.keysetBoundary ? PAGE_SIZE + 1 : PAGE_SIZE;
          var previousBoundary = self.state.keysetBoundary;

          if (oldestCreated) self.state.keysetBoundary = oldestCreated;

          if (appended === 0 && previousBoundary) {
            self.state.keysetBoundary = new Date(
              new Date(previousBoundary).getTime() - 1
            ).toISOString();
          }

          var tierFinished = rawCount < requestedSize;
          if (self.state.sort !== 'recent' && tierFinished) {
            var nextTier =
              self.state.sort === 'highest'
                ? self.state.ratingTier - 1
                : self.state.ratingTier + 1;
            self.state.ratingTier = nextTier;
            self.state.keysetBoundary = null;
            self.state.hasMore = nextTier >= 4 && nextTier <= 5;
          } else {
            self.state.hasMore = rawCount > 0;
          }
        } else {
          if (!self.proxyEndpoint && oldestCreated) {
            // Seed the keyset fallback from the last successful cursor page.
            self.state.keysetBoundary = oldestCreated;
          }
          self.state.nextCursor = normalized.nextCursor;
          self.state.hasMore = !!normalized.nextCursor;
        }

        return appended;
    }

    function requestWithCursorFallback(requestUrl) {
      return self.fetchJson(requestUrl).then(processPayload).catch(function (err) {
        if (
          !self.proxyEndpoint &&
          !self.state.keysetMode &&
          self.state.sort === 'recent' &&
          err &&
          err.status >= 500
        ) {
          console.warn(
            '[ReviewsPage] Klaviyo cursor failed; using created-date keyset pagination.'
          );
          self.state.keysetMode = true;
          self.state.nextCursor = null;
          return self.fetchJson(self.buildKeysetReviewsUrl()).then(processPayload);
        }
        throw err;
      });
    }

    var featuredMediaPromise = Promise.resolve();
    if (
      isInitial &&
      !this.proxyEndpoint &&
      this.state.sort === 'recent'
    ) {
      // Add a small media showcase first without changing the main feed's
      // cursor/keyset state. Main pagination then continues normally and the
      // shared seenIds guard removes any overlap.
      featuredMediaPromise = this.fetchJson(this.buildFeaturedMediaUrl())
        .then(function (payload) {
          var featured = self.normalizeKlaviyoPayload(payload);
          featured.reviews.forEach(function (review) {
            if (!review || !review.id || Number(review.rating) < 4) return;
            if (self.state.seenIds[review.id]) return;
            self.state.seenIds[review.id] = true;
            self.els.list.insertAdjacentHTML('beforeend', self.renderCard(review));
          });
        })
        .catch(function (err) {
          // Media showcase is optional; never block the normal review feed.
          console.warn(
            '[ReviewsPage] featured media reviews could not load',
            err && err.message
          );
        });
    }

    featuredMediaPromise
      .then(function () {
        return requestWithCursorFallback(url);
      })
      .then(function () {
        var hasAny = self.els.list && self.els.list.children.length > 0;
        if (!hasAny) {
          self.setStateVisibility('empty');
          if (self.els.empty) self.els.empty.textContent = self.emptyMessage;
        } else {
          self.setStateVisibility(null);
          if (self.els.loading) self.els.loading.hidden = true;
          if (self.els.error) self.els.error.hidden = true;
          if (self.els.empty) self.els.empty.hidden = true;
        }

        if (self.els.loadMore) {
          self.els.loadMore.hidden = !self.state.hasMore;
        }
      })
      .catch(function (err) {
        console.error('[ReviewsPage] reviews request failed', err && err.message);
        if (isInitial || !(self.els.list && self.els.list.children.length)) {
          self.showError(self.errorMessage);
        }
      })
      .then(function () {
        self.state.loading = false;
        if (self.els.loadMore) {
          self.els.loadMore.classList.remove('is-loading');
          self.els.loadMore.setAttribute('aria-busy', 'false');
        }
      });
  };

  ReviewsPage.prototype.renderReviewMedia = function (images) {
    if (!images || !images.length) return '';
    var parts = ['<div class="crp__review-media" aria-label="Customer photos">'];
    images.forEach(function (url) {
      if (isVideoUrl(url)) {
        parts.push(
          '<video class="crp__review-media-item" src="' +
            escapeHtml(url) +
            '" controls playsinline preload="metadata"></video>'
        );
      } else {
        parts.push(
          '<a class="crp__review-media-link" href="' +
            escapeHtml(url) +
            '" target="_blank" rel="noopener noreferrer">' +
            '<img class="crp__review-media-item" src="' +
            escapeHtml(url) +
            '" alt="Customer review photo" loading="lazy">' +
            '</a>'
        );
      }
    });
    parts.push('</div>');
    return parts.join('');
  };

  ReviewsPage.prototype.renderCard = function (review) {
    var product = review.product || {};
    var href = productHref(product);
    var name = product.name || '';
    var title = review.title ? String(review.title).trim() : '';
    var content = review.content ? String(review.content).trim() : '';
    var author = review.author ? String(review.author).trim() : '';
    var date = formatDate(review.created);
    var reviewImages = review.images || [];

    var parts = [];
    parts.push(
      '<article class="crp__card' +
        (reviewImages.length ? ' crp__card--has-media' : '') +
        '" data-review-id="' +
        escapeHtml(review.id) +
        '">'
    );

    parts.push('<div class="crp__card-heading">');
    if (name) {
      parts.push('<span class="crp__about">about </span>');
      parts.push(
        href
          ? '<a class="crp__product-name" href="' + escapeHtml(href) + '">' + escapeHtml(name) + '</a>'
          : '<span class="crp__product-name">' + escapeHtml(name) + '</span>'
      );
    }
    if (date) {
      parts.push(
        '<time class="crp__card-date" datetime="' +
          escapeHtml(review.created) +
          '">' +
          escapeHtml(date) +
          '</time>'
      );
    }
    parts.push('</div>');

    parts.push('<div class="crp__card-top">');
    if (review.rating != null && review.rating !== '') {
      parts.push(
        '<div class="crp__card-stars" aria-label="' +
          escapeHtml(String(review.rating)) +
          ' out of 5 stars">' +
          starsMarkup(review.rating) +
          '</div>'
      );
    }
    parts.push('</div>');

    if (author || review.verified) {
      parts.push('<div class="crp__reviewer">');
      if (author) {
        parts.push('<span class="crp__reviewer-name">' + escapeHtml(author) + '</span>');
      }
    }
    if (review.verified) {
      parts.push(
        '<span class="crp__verified">' +
          '<svg class="crp__verified-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
          '<path fill="currentColor" d="M8 0l1.8 1.2L12 1l.8 2.2L15 4.5l-.7 2.3.7 2.3-2.2 1.3L12 13l-2.2-.2L8 14.5l-1.8-1.7L4 13l-.8-2.2L1 9.1l.7-2.3L1 4.5l2.2-1.3L4 1l2.2.2L8 0zm-.1 10.6l3.8-3.8-.9-.9-2.9 2.9-1.4-1.4-.9.9 2.3 2.3z"/>' +
          '</svg>' +
          escapeHtml(this.verifiedLabel) +
          '</span>'
      );
    }
    if (author || review.verified) parts.push('</div>');

    parts.push(this.renderReviewMedia(reviewImages));

    if (title) {
      parts.push('<h3 class="crp__card-title">' + escapeHtml(title) + '</h3>');
    }
    if (content) {
      parts.push('<p class="crp__card-content">' + escapeHtml(content) + '</p>');
    }

    parts.push('</article>');
    return parts.join('');
  };

  function boot() {
    document.querySelectorAll('[data-reviews-page]').forEach(function (el) {
      if (el.__rpMounted) return;
      el.__rpMounted = true;
      new ReviewsPage(el).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
